import { insert, remove } from "ramda";
import React from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { Field } from "src/common/components/Field";
import {
	AddButton,
	AddFirstButton,
	FieldList,
	FieldListInput,
	FieldListItem,
	RemoveButton
} from "src/common/components/FieldList";
import { UnknownInputRenderer } from "src/features/forms/components/UnknownInputRenderer";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import { NodeContextProvider } from "src/features/forms/context/NodeContext";
import {
	useError,
	useMaxItems,
	useMinItems,
	useRequired,
	useValue
} from "src/features/forms/hooks/useField";
import { FieldListData } from "src/features/forms/types";

export const FieldListRenderer: React.FunctionComponent<FieldListData> =
	React.memo(props => (
		<NodeContextProvider path={props.path}>
			<ContentRenderer {...props} />
		</NodeContextProvider>
	));

const ContentRenderer: React.FunctionComponent<
	Pick<FieldListData, "label" | "help" | "errorMessages" | "input" | "path">
> = React.memo(
	({ label: labelMsg, help: helpMsg, errorMessages, input, path }) => {
		const { formatMessage } = useIntl();

		const required = useRequired();

		const label = formatMessage(labelMsg);
		const help = helpMsg === undefined ? null : formatMessage(helpMsg);

		const isLocked = React.useContext(IsLockedContext);

		const minItems = useMinItems();
		const maxItems = useMaxItems();

		const [values, setValues] = useValue<(string | undefined)[]>([]);

		if (values.length === 0) {
			return (
				<Field required={required} label={`1. ${label}`} help={help}>
					<AddFirstButton
						disabled={isLocked}
						onClick={() => {
							setValues([undefined]);
						}}
					/>
				</Field>
			);
		}

		return (
			<FieldList path={path}>
				{values.map((_, i) => (
					<NodeContextProvider key={i} path={[i]}>
						<ItemRenderer
							required={required}
							label={`${i + 1}. ${label}`}
							help={help}
							errorMessages={errorMessages}
						>
							<FieldListInput>
								<UnknownInputRenderer {...input} />
							</FieldListInput>
							<RemoveButton
								disabled={isLocked || values.length <= minItems}
								onClick={() => {
									setValues(remove(i, 1, values));
								}}
							/>
							<AddButton
								disabled={isLocked || values.length >= maxItems}
								onClick={() => {
									setValues(insert(i + 1, undefined, values));
								}}
							/>
						</ItemRenderer>
					</NodeContextProvider>
				))}
			</FieldList>
		);
	}
);

const ItemRenderer: React.FunctionComponent<
	React.PropsWithChildren<{
		required: boolean;
		label: React.ReactNode;
		help: React.ReactNode;
		errorMessages: {
			[key: string]: MessageDescriptor;
		};
	}>
> = React.memo(({ required, label, help, errorMessages, children }) => {
	const error = useError(errorMessages);
	return (
		<FieldListItem>
			<Field required={required} label={label} help={help} error={error}>
				{children}
			</Field>
		</FieldListItem>
	);
});
